import { useState, useEffect, useCallback } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import { connectSocket, SocketRoom } from 'services/socket'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import ItemTabs from 'components/ItemTabs'
import Connection from './Connection'
import Chats from './Chats'
import Style from './Style'

import { PageContainer, PageTitle, TabStickyContainer, Container, Content, Caption } from './styles'

const TABS = [
  { label: 'Conversas', value: 'chats' },
  { label: 'Conexão', value: 'connection' },
  { label: 'Jeito de atender', value: 'style' }
]

/*
  Gestão WhatsApp: conectar o número do salão, ligar o bot, acompanhar e assumir as conversas e
  escolher o jeito de atender. Em teste, fora de produção (`services/features.js`).

  A profissional com a permissão `whatsapp` vê só a aba Conversas, do número do salão.
*/
export default function Whatsapp() {
  const businessId = AuthService.getBusinessId()
  const salonName = AuthService.getUser()?.name
  const professional = AuthService.getUserType() === 'professional'
  const [session, setSession] = useState(undefined)
  const [socket, setSocket] = useState(null)
  const [selectedTab, setSelectedTab] = useState(() => {
    try {
      return localStorage.getItem('whatsapp_tab') ?? 'chats'
    } catch {
      return 'chats'
    }
  })

  const loadSession = useCallback(() => {
    cutileiApi.get(`/businesses/${businessId}/whatsapp/session`)
      .then(({ data }) => setSession(data))
      .catch(err => {
        if (err?.response?.status !== 404) logApiError(err, 'whatsapp.session')
        setSession(null)
      })
  }, [businessId])

  useEffect(() => {
    loadSession()
  }, [loadSession])

  // Um socket para a página: status da conexão aqui, mensagens e modos nas conversas.
  // Status novo relê a sessão inteira: o evento não traz o número nem a hora da conexão.
  useEffect(() => {
    const s = connectSocket({ token: AuthService.getToken(), rooms: [SocketRoom.whatsapp(businessId)] })
    s.on(`${businessId}_whatsapp_session`, loadSession)
    setSocket(s)
    return () => { s.disconnect() }
  }, [businessId, loadSession])

  const connected = session?.status === 'WORKING'
  // Sem WhatsApp conectado, as conversas não têm o que mostrar: a tela abre na conexão
  // A profissional com a permissão `whatsapp` só atende as conversas: conectar e o jeito de atender são do dono
  const tab = professional ? 'chats' : !connected && selectedTab === 'chats' ? 'connection' : selectedTab

  const changeTab = value => {
    setSelectedTab(value)
    try {
      localStorage.setItem('whatsapp_tab', value)
    } catch {
      // navegador sem localStorage: só não lembra a aba
    }
  }

  return (
    <PageContainer>
      <BusinessNavbar/>
      <PageTitle>WhatsApp</PageTitle>
      {/* A profissional só tem as conversas: sem abas para escolher */}
      {!professional && (
        <TabStickyContainer>
          <ItemTabs
            tabs={TABS.map(t => ({ ...t, disabled: t.value === 'chats' && !connected }))}
            selectedTab={tab}
            tabWidth={150}
            onTabChange={changeTab}
          />
        </TabStickyContainer>
      )}
      <Container>
        <Content $size={{ chats: 'full', style: 'wide' }[tab]}>
          {session === undefined && <Caption>Carregando...</Caption>}
          {session !== undefined && tab === 'connection' && (
            <Connection businessId={businessId} session={session} onSessionChange={setSession}/>
          )}
          {session !== undefined && tab === 'chats' && professional && !connected && (
            <Caption>O WhatsApp do salão não está conectado. Peça para o responsável pelo salão conectar o número.</Caption>
          )}
          {session !== undefined && tab === 'chats' && (connected || !professional) && (
            <Chats businessId={businessId} socket={socket} session={session}/>
          )}
          {session !== undefined && tab === 'style' && <Style businessId={businessId} salonName={salonName}/>}
        </Content>
      </Container>
    </PageContainer>
  )
}
